/**
 * @fileoverview Custom Quill blot for rendering merge fields in rich text documents.
 * Blots are the objects that represent types of content and formatting available in a Quill editor.
 *
 * This module defines a custom embed blot that allows merge fields to be inserted and
 * rendered within Quill editor content. Merge fields are rendered as styled span elements
 * with data attributes that preserve field metadata.
 *
 * @module MergeFieldBlot
 */

import Quill from 'quill';
import type { EmbedBlot } from 'parchment';
import { MergeField, MergeNodeElement } from '@k12/models';

const Embed = Quill.import('blots/embed') as typeof EmbedBlot;

/**
 * Custom Quill blot for rendering merge fields as embedded elements in rich text content.
 *
 * Merge fields represent placeholders that are replaced with actual values during document generation.
 * This blot handles the creation, rendering, and value extraction of merge fields within the Quill editor.
 *
 * @class MergeFieldBlot
 * @extends {EmbedBlot}
 *
 * @example
 * // Register the blot with Quill
 * Quill.register('blots/mergeField', MergeFieldBlot);
 *
 * // Insert a merge field
 * quill.insertEmbed(0, 'mergeField', {
 *   value: 'student.firstName',
 *   text: 'Student First Name',
 *   url: ''
 * });
 */
class MergeFieldBlot extends Embed {
  /** The name used to identify this blot type in Quill's registry */
  static override readonly blotName = 'mergeField';

  /** CSS class applied to merge field elements for styling */
  static override readonly className = 'merge-field';

  /** HTML tag name used to render the merge field */
  static override readonly tagName = 'span';

  /**
   * Creates a DOM node for the merge field with embedded metadata.
   *
   * Generates a span element containing data attributes for the field's value,
   * URL (if applicable), and display text. The display text is rendered as the
   * visible content within the editor.
   *
   * This method handles two scenarios:
   * 1. Programmatic insertion: Receives a MergeField object with placeHolder property
   * 2. HTML parsing: Receives a MergeNodeElement (parsed from dangerouslyPasteHTML) with only value property
   *
   * When placeHolder is unavailable (HTML parsing case), falls back to using the value property
   * as the placeholder text.
   *
   * @param {MergeField | MergeNodeElement} [value] - The merge field configuration object
   * @param {string} [value.value] - The field identifier (e.g., 'Usr_FirstName', 'student.firstName')
   * @param {string} [value.text] - The human-readable display text
   * @param {string} [value.url] - Optional URL associated with the field
   * @param {string} [value.placeHolder] - The placeholder text (MergeField only)
   * @returns {Node} The created DOM node (span element with inner span containing data attributes)
   *
   * @override
   */
  static override create(value?: MergeField | MergeNodeElement): Node {
    const element = super.create() as Element;
    // Handle both MergeField (with placeHolder) and MergeNodeElement (from parsed HTML)
    const placeHolder = (value as MergeField)?.placeHolder || value?.value || '';

    // Use triple braces only for placeholders that represent HTML content.
    const useTripleBraces = (value as MergeField)?.mergeFieldTypeId === 2; // 2 represents HTML type
    const startBraces = useTripleBraces ? '{{{' : '{{';
    const endBraces = useTripleBraces ? '}}}' : '}}';
    const renderedPlaceHolder = `${startBraces}${placeHolder}${endBraces}`;

    element.innerHTML = `<span data-value="${value?.value || ''}" data-url="${value?.url || ''}" data-text="${value?.text || ''}" data-merge-field-type-id="${(value as MergeField)?.mergeFieldTypeId || 1}" class="merge-field-text">${renderedPlaceHolder}</span>`;
    return element;
  }

  /**
   * Extracts merge field data from a DOM node.
   *
   * Reads the data attributes from the HTML element and reconstructs the merge field
   * object with its value, text, and URL properties.
   *
   * @param {HTMLElement} node - The DOM node to extract data from
   * @returns {MergeNodeElement} The merge field data object
   *
   * @override
   */
  static override value(node: HTMLElement): MergeNodeElement {
    // Look for the inner span that contains the data attributes
    const innerSpan = (node.querySelector('.merge-field-text') as HTMLElement) || node;
    return {
      value: innerSpan.dataset['value'] || null,
      text: innerSpan.dataset['text'] || null,
      url: innerSpan.dataset['url'] || null,
      mergeFieldTypeId: Number.parseInt(innerSpan.dataset['mergeFieldTypeId'] || '1', 10) // Default to 1 (text) if not specified
    };
  }
}

export default MergeFieldBlot;
